import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import AutoCompleteViewModel from '@/ViewModels/Controls/AutoCompleteViewModel';
import KendoDropDownHelper from '@/Utility/KendoDropDownHelper';

export default class AutoComplete extends Control {

    private jQueryControl: JQuery;

    /**
     * Interner Merker, ob das Control geleert wurde
     */
    private wasCleared: boolean;

    /**
     * @override
     */
    public widget: KnockoutObservable<kendo.ui.AutoCompleteExt>;


    public constructor(private viewModel: AutoCompleteViewModel, private element?: JQuery) {
        super(viewModel, element);
        this.wasCleared = false;
        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div>').append('<input type="text">');

        this.applyBindings(this.jQueryControl.find('input'), {
            locator: this.viewModel.locator,

            kendoAutoComplete: {
                data: this.viewModel.data,

                value: this.viewModel.value,

                minLength: this.viewModel.minLength,

                filtering: (e) => {
                    let filter = e.filter;
                    if (filter == null || filter.value == null || filter.value.length < this.viewModel.minLength()) {
                        this.widget().close();
                    }
                },

                enabled: this.viewModel.isEnabled,

                select: this.viewModel.onSelect,

                filter: this.viewModel.filter,

                placeholder: this.viewModel.placeholder,

                widget: this.widget
            }
        });

        return this.jQueryControl;
    }

    private onWidgetCreatedCallback(widget: kendo.ui.AutoCompleteExt): void {
        this.applyBindings(this.jQueryControl, {
            controlName: 'autocomplete',

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            tooltip: this.viewModel.tooltip,

            invalid: this.viewModel.invalidationText,

            visible: this.viewModel.isVisible,

            styles: this.definePureComputed({
                read: () => {
                    let styles = ['default'];

                    return styles;
                }
            }),

            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    if (this.viewModel.isValid() === true) {
                        states.push('valid');
                    } else {
                        states.push('invalid');
                    }

                    if (this.viewModel.isEnabled() === true) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    return states;
                }
            })
        });

        if (this.viewModel.maxLength() >= 0) {
            this.jQueryControl.find('input').attr('maxlength', this.viewModel.maxLength);
        }

        if (this.viewModel.ariaLabel) {
            this.jQueryControl.find('input').attr('aria-label', this.viewModel.ariaLabel);
        }

        this.applyBindings(this.jQueryControl.find('input'), {
            textInput: this.viewModel.value
        });

        // Automatische Suche beim Fokus
        if (this.viewModel.searchOnFocus) {
            this.addEventHandler('focus',
                this.jQueryControl,
                () => {
                    this.widget().search('');
                    this.widget().popup.open();
                }, 'input');
        }

        if (this.viewModel.clearOnFocusLost) {
            this.addEventHandler('focusout',
                this.jQueryControl,
                () => {
                    this.widget().value('');
                }, 'input');
        }

        this.registerEventsForPreventOpeningOnClearing();

        this.defineComputed(() => {
            let data = this.viewModel.data();
            if (ko.computedContext.isInitial()) {
                // Bei der Initialisierung des Controls sollen nicht direkt die Vorschläge angezeigt werden
                return;
            }

            this.async(() => {
                let value = widget.value();
                let minlength = this.viewModel.minLength();

                if (value.length >= minlength) {
                    if (widget.popup.visible()) {
                        widget.search(value);
                    }
                }
            }, 50);
        });



        if (this.viewModel.onKeyUp != null) {
            this.addEventHandler('keyup', this.jQueryControl, (event) => {
                if (this.isValidKeyCode(event.keyCode)) {
                    let value = $(event.target).val();

                    this.viewModel.onKeyUp(value);
                }
            }, 'input');
        }

        if (this.viewModel.onClick != null) {
            this.addEventHandler('click', this.jQueryControl, () => {
                this.viewModel.onClick();
            });
        }

        if (this.viewModel.onSubmit != null) {
            this.addEventHandler('keypress', this.jQueryControl, (event) => {
                if (event.key === 'Enter') {
                    this.viewModel.onSubmit(widget.value());
                }
            });
        }

        if (this.viewModel.onFocus != null) {
            this.addEventHandler('focus', this.jQueryControl, () => {
                this.viewModel.onFocus();
            });
        }

        // Kendo-Überschreibungen
        widget._resizePopup = this.resetDropDownPopupPosition.bind(this);
        const oldOpen = widget.popup.open;
        widget.popup.open = (...params) => {
            oldOpen.apply(widget.popup, params);
            this.resetDropDownPopupPosition();
        }
    }

    /**
     * Registriert Events und Subscriptions, die dazu dienen, dass das Popup sich beim Leeren nicht von selbst öffnet
     */
    private registerEventsForPreventOpeningOnClearing(): void {
        this.defineSubscription(this.viewModel.isClearing,
            (isClearing) => {
                // Merken, dass Clear aufgerufen wurde
                if (isClearing)
                    this.wasCleared = true;
            });
        this.widget().bind('open',
            (e) => {
                // Wurd zuletzt das Control geleert und soll nun geöffnet werden?
                if (this.wasCleared) {
                    // Öffnen verhindern und "Merker" zurücksetzen
                    e.preventDefault();
                    this.wasCleared = false;
                }
            });
    }

    private resetDropDownPopupPosition() {
        const labels = this.viewModel.data().map((value: string) => {
            return value;
        });

        KendoDropDownHelper.resetDropDownPopupPosition(labels, this.jQueryControl, this.widget(), 0);
    }

    private isValidKeyCode(keyCode: number): boolean {
        return (keyCode > 47 && keyCode < 58) || // number keys
            (keyCode == 32) || // spacebar
            (keyCode > 64 && keyCode < 91) || // letter keys
            (keyCode > 95 && keyCode < 112) || // numpad keys
            (keyCode > 185 && keyCode < 193) || // ;=,-./` (in order)
            (keyCode > 218 && keyCode < 223);   // [\]' (in order)
    }
}

ComponentManager.setControl('AutoComplete', AutoComplete, 'RADPresentation.HTMLClient.DefaultViewExtensions');